/* The Overview's main picture: the estate's quality over a window a reader chooses.
 *
 * **One series, so no legend box** — the panel title names what is plotted, which is the data-viz
 * rule for a single series. A 2px line with a ~10% wash, solid hairline gridlines, the last reading
 * labelled directly at the end of the line, and a ringed end dot. Text never wears the series colour:
 * the end label, the axes and the tooltip all use text tokens, and the line beside them carries
 * identity.
 *
 * **The window control is real.** `GET /v1/scoring/estate/trend` takes `days` between 2 and 366 and
 * `useEstateTrend` keys its cache on it, so 7 / 30 / 90 are three genuine reads rather than one series
 * cropped three ways. While a new window loads, the previous line stays on screen at reduced opacity —
 * "refetch keeps the frame" — so switching never flashes a skeleton or jumps the layout. The dates on
 * the axis say which window is drawn, so the held frame cannot be mistaken for the new one.
 *
 * **The hover layer finds a date, not a pixel.** The crosshair snaps to the nearest reading, the same
 * readout is reachable from the keyboard (arrow keys, Home, End), and every value is also in a
 * visually hidden table — the tooltip enhances and never gates.
 *
 * **Drawn with GSAP once per series**: grid, then the line drawing left to right, the wash, the end
 * dot, and one halo pulse. A new window is a new series and draws again; a refetch of the same window
 * is not, and does not.
 */

import type { ReactElement, RefObject } from 'react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEstateTrend } from '@/features/scoring';
import type { EstatePoint } from '@/shared/api/generated/scoring';
import { EASE, useReveal } from '@/shared/ui/motion';

import { nearestPoint, trendGeometry, trendSnapshot } from '../model/trend';
import type { PlotBox, PlottedPoint, TrendGeometry } from '../model/trend';
import { Panel, Skeleton } from './OverviewParts';

/** The windows offered. Each is inside the route's 2..366 bound. */
export const TREND_WINDOWS = [7, 30, 90] as const;
export type TrendWindow = (typeof TREND_WINDOWS)[number];

const PLOT_HEIGHT = 236;
/* The right margin holds the end label, so the last reading is never drawn under its own number. */
const MARGINS = { top: 16, right: 52, bottom: 30, left: 40 } as const;
/* jsdom measures nothing, and the first paint before measurement must still be a plausible width. */
const FALLBACK_WIDTH = 640;

/** A reading's day for an axis or a tooltip, in the reader's locale, read as UTC so no timezone can
 * move it by one. */
function formatDay(day: string, withYear = false): string {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

const formatScore = (value: number): string => value.toFixed(1);

/** The width the plot actually has, so the SVG is drawn 1:1 and its dots stay round. */
function useMeasuredWidth<T extends HTMLElement>(): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return undefined;
    if (element.clientWidth > 0) setWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured !== undefined && measured > 0) setWidth(Math.round(measured));
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  return [ref, width];
}

function TrendPlot({
  geometry,
  width,
  dimmed,
}: {
  readonly geometry: TrendGeometry;
  readonly width: number;
  readonly dimmed: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const gradient = `lq-ov-trend-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
  const points = geometry.points;
  const end = points[points.length - 1];
  const hovered = active === null ? undefined : points[active];

  const svg = useReveal<SVGSVGElement>(
    true,
    (root, timeline) => {
      timeline
        .from(root.querySelectorAll('.lq-ov-trend__gridline'), {
          opacity: 0,
          duration: 0.3,
          stagger: 0.04,
        })
        .fromTo(
          root.querySelector('.lq-ov-trend__line'),
          { strokeDasharray: '1 1', strokeDashoffset: 1 },
          { strokeDashoffset: 0, duration: 0.9, ease: EASE.draw },
          0.1,
        )
        .from(
          root.querySelector('.lq-ov-trend__area'),
          { opacity: 0, duration: 0.6 },
          0.45,
        )
        .from(
          root.querySelector('.lq-ov-trend__end'),
          { scale: 0, transformOrigin: '50% 50%', duration: 0.3 },
          0.9,
        )
        .from(
          root.querySelector('.lq-ov-trend__endLabel'),
          { opacity: 0, x: -4, duration: 0.3 },
          0.95,
        )
        .fromTo(
          root.querySelector('.lq-ov-trend__halo'),
          { scale: 0.6, opacity: 0.5, transformOrigin: '50% 50%' },
          { scale: 2, opacity: 0, duration: 0.9, ease: 'power2.out' },
          1,
        );
    },
    'trend',
  );

  const toIndex = (x: number): number | null => {
    const point = nearestPoint(points, x);
    return point === undefined ? null : points.indexOf(point);
  };

  return (
    <div
      className={
        dimmed ? 'lq-ov-trend__canvas lq-ov-trend__canvas--held' : 'lq-ov-trend__canvas'
      }
      /* A group a keyboard can enter: arrows walk the readings, Home and End jump, Escape leaves.
         The same readout the pointer gets, so nothing is mouse-only. */
      role="group"
      tabIndex={0}
      aria-label={t('home.trend.chartLabel', { count: points.length })}
      onKeyDown={(event) => {
        const last = points.length - 1;
        const current = active ?? last;
        const next =
          event.key === 'ArrowLeft'
            ? Math.max(0, current - 1)
            : event.key === 'ArrowRight'
              ? Math.min(last, current + 1)
              : event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? last
                  : undefined;
        if (event.key === 'Escape') setActive(null);
        if (next === undefined) return;
        event.preventDefault();
        setActive(next);
      }}
      onBlur={() => {
        setActive(null);
      }}
    >
      <svg
        ref={svg}
        className="lq-ov-trend__svg"
        width={width}
        height={PLOT_HEIGHT}
        viewBox={`0 0 ${String(width)} ${String(PLOT_HEIGHT)}`}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" className="lq-ov-trend__washTop" />
            <stop offset="1" className="lq-ov-trend__washBottom" />
          </linearGradient>
        </defs>

        {geometry.valueTicks.map((tick) => (
          <g key={tick.value}>
            <line
              className="lq-ov-trend__gridline"
              x1={MARGINS.left}
              x2={width - MARGINS.right}
              y1={tick.y}
              y2={tick.y}
            />
            <text
              className="lq-ov-trend__tick"
              x={MARGINS.left - 10}
              y={tick.y}
              dy="0.32em"
              textAnchor="end"
            >
              {Number.isInteger(tick.value) ? String(tick.value) : tick.value.toFixed(1)}
            </text>
          </g>
        ))}
        {geometry.dayTicks.map((tick, index) => (
          <text
            key={tick.day}
            className="lq-ov-trend__tick lq-ov-trend__tick--day"
            x={tick.x}
            y={PLOT_HEIGHT - 8}
            textAnchor={
              index === 0
                ? 'start'
                : index === geometry.dayTicks.length - 1
                  ? 'end'
                  : 'middle'
            }
          >
            {formatDay(tick.day)}
          </text>
        ))}

        <path
          className="lq-ov-trend__area"
          d={geometry.area}
          fill={`url(#${gradient})`}
        />
        <path className="lq-ov-trend__line" d={geometry.line} pathLength={1} />

        {end === undefined ? null : (
          <g>
            <circle className="lq-ov-trend__halo" cx={end.x} cy={end.y} r={6} />
            <circle className="lq-ov-trend__end" cx={end.x} cy={end.y} r={4.5} />
            <text className="lq-ov-trend__endLabel" x={end.x + 10} y={end.y} dy="0.32em">
              {formatScore(end.value)}
            </text>
          </g>
        )}

        {hovered === undefined ? null : (
          <g className="lq-ov-trend__cross">
            <line
              className="lq-ov-trend__crossline"
              x1={hovered.x}
              x2={hovered.x}
              y1={MARGINS.top}
              y2={geometry.baseline}
            />
            <circle
              className="lq-ov-trend__crossdot"
              cx={hovered.x}
              cy={hovered.y}
              r={4.5}
            />
          </g>
        )}

        {/* The hit area is the whole plot, not the two-pixel line: a reader aims at a date. */}
        <rect
          className="lq-ov-trend__hit"
          x={MARGINS.left}
          y={0}
          width={Math.max(0, width - MARGINS.left - MARGINS.right)}
          height={PLOT_HEIGHT}
          onPointerMove={(event) => {
            const box = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
            if (box !== undefined) setActive(toIndex(event.clientX - box.left));
          }}
          onPointerLeave={() => {
            setActive(null);
          }}
        />
      </svg>

      {hovered === undefined ? null : <Readout point={hovered} width={width} />}
    </div>
  );
}

/** The tooltip: value first and strongest, the date and the population behind it after. */
function Readout({
  point,
  width,
}: {
  readonly point: PlottedPoint;
  readonly width: number;
}): ReactElement {
  const { t } = useTranslation();
  /* Kept inside the plot: flipped to the left of the crosshair near the right edge, so the readout
     never hangs off the card. */
  const flip = point.x > width - 180;
  return (
    <div
      className={
        flip ? 'lq-ov-trend__readout lq-ov-trend__readout--left' : 'lq-ov-trend__readout'
      }
      style={{ left: point.x, top: Math.max(4, point.y - 16) }}
      role="status"
    >
      <strong className="lq-ov-trend__readoutValue">{formatScore(point.value)}</strong>
      <span className="lq-ov-trend__readoutDay">{formatDay(point.day, true)}</span>
      <span className="lq-ov-trend__readoutMeta">
        {t('home.trend.scoredThatDay', { count: point.scored })}
      </span>
    </div>
  );
}

/** Latest, highest and lowest, from the series itself — the reference's snapshot column. */
function Snapshot({
  series,
}: {
  readonly series: readonly EstatePoint[];
}): ReactElement | null {
  const { t } = useTranslation();
  const snapshot = trendSnapshot(series);
  if (snapshot === null) return null;
  const rows = [
    { key: 'latest', point: snapshot.latest },
    { key: 'high', point: snapshot.high },
    { key: 'low', point: snapshot.low },
  ] as const;
  return (
    <dl className="lq-ov-trend__snapshot">
      {rows.map(({ key, point }) => (
        <div key={key} className="lq-ov-trend__stat">
          <dt className="lq-ov-trend__statLabel">{t(`home.trend.stat.${key}`)}</dt>
          <dd className="lq-ov-trend__statValue">{formatScore(point.value)}</dd>
          <dd className="lq-ov-trend__statDay">{formatDay(point.day)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function QualityTrend(): ReactElement {
  const { t } = useTranslation();
  const [days, setDays] = useState<TrendWindow>(30);
  const trend = useEstateTrend(days);
  const [plotRef, width] = useMeasuredWidth<HTMLDivElement>();

  /* The last series that answered, held so a window switch keeps the frame. Set in an effect rather
     than during render, so a render never writes state it then reads. */
  const [held, setHeld] = useState<{
    readonly days: number;
    readonly items: readonly EstatePoint[];
  } | null>(null);
  useEffect(() => {
    if (trend.isSuccess) setHeld({ days, items: trend.data.items });
  }, [trend.isSuccess, trend.data, days]);

  const series = trend.isSuccess ? trend.data.items : (held?.items ?? null);
  const dimmed = !trend.isSuccess && held !== null && trend.isPending;
  const box: PlotBox = { width, height: PLOT_HEIGHT, ...MARGINS };
  const geometry = series === null ? null : trendGeometry(series, box);
  /* A new series is a new drawing; the same series re-rendered is not. */
  const identity =
    series === null || series.length === 0
      ? 'none'
      : `${String(trend.isSuccess ? days : (held?.days ?? days))}:${series[0]?.day ?? ''}:${series[series.length - 1]?.day ?? ''}:${String(series.length)}`;

  const windows = (
    <div className="lq-ov-window" role="group" aria-label={t('home.trend.windowLabel')}>
      {TREND_WINDOWS.map((window) => (
        <button
          key={window}
          type="button"
          className="lq-ov-window__option"
          aria-pressed={window === days}
          aria-label={t('home.trend.windowDays', { days: window })}
          onClick={() => {
            setDays(window);
          }}
        >
          {t('home.trend.windowShort', { days: window })}
        </button>
      ))}
    </div>
  );

  return (
    <Panel
      title={t('home.trend.title')}
      subtitle={t('home.trend.subtitle')}
      actions={windows}
      busy={trend.isPending && held === null}
      className="lq-ov-trend"
    >
      <div className="lq-ov-trend__body">
        <div className="lq-ov-trend__plot" ref={plotRef}>
          {series === null ? (
            trend.isError ? (
              <p className="lq-ov-note" title={trend.error.message}>
                {t('home.trend.unreadable')}
              </p>
            ) : (
              <Skeleton label={t('home.trend.loading')} lines={['block']} />
            )
          ) : geometry === null ? (
            <p className="lq-ov-note">
              {t('home.trend.tooShort')}{' '}
              <span className="lq-ov-note__hint">{t('home.trend.tooShortHint')}</span>
            </p>
          ) : (
            <TrendPlot key={identity} geometry={geometry} width={width} dimmed={dimmed} />
          )}
        </div>
        {series === null || series.length === 0 ? null : <Snapshot series={series} />}
      </div>

      {/* The table twin: every plotted value, reachable without hovering or seeing the chart. */}
      {series === null || series.length === 0 ? null : (
        <table className="lq-visually-hidden">
          <caption>{t('home.trend.tableCaption', { days })}</caption>
          <thead>
            <tr>
              <th scope="col">{t('home.trend.tableDay')}</th>
              <th scope="col">{t('home.trend.tableScore')}</th>
              <th scope="col">{t('home.trend.tableScored')}</th>
            </tr>
          </thead>
          <tbody>
            {series.map((point) => (
              <tr key={point.day}>
                <td>{formatDay(point.day, true)}</td>
                <td>{formatScore(point.value)}</td>
                <td>{point.scored}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
